import { Card, CardAction, CardContent, CardDescription, CardHeader } from '../ui/card';
import { PanelTitle } from '../layout/Typography';
import { cn } from '../../lib/utils';
import { DescriptionHint } from './DescriptionHint';

interface SectionCardProps {
  /**
   * Heading text or a small node tree (e.g. title plus inline badges) that
   * `PanelTitle` will render inside an `<h2>`/`<h3>`. Keep it short; long
   * structures belong in the body, not the header.
   */
  title: React.ReactNode;
  /**
   * Subtitle rendered below the heading. Accepts either a plain string or a
   * node tree, so an inline link or icon can appear inside the description
   * without dropping back to a hand-rolled card.
   */
  description?: React.ReactNode;
  icon?: React.ReactNode;
  action?: React.ReactNode;
  as?: 'h2' | 'h3';
  variant?: 'default' | 'destructive';
  children: React.ReactNode;
  className?: string;
  headerClassName?: string;
  contentClassName?: string;
  /** Lands on the root Card element; lets callers preserve stable selectors. */
  'data-testid'?: string;
}

export function SectionCard({
  title,
  description,
  icon,
  action,
  as = 'h2',
  variant = 'default',
  children,
  className,
  headerClassName,
  contentClassName,
  'data-testid': dataTestId,
}: SectionCardProps) {
  // Destructive cards are a single action (delete) plus a warning. Keep that
  // copy visible below `sm` instead of collapsing it into DescriptionHint.
  const collapseDescriptionOnMobile = variant !== 'destructive';

  const titleNode = (
    <div className="flex min-w-0 items-center gap-1">
      <PanelTitle
        as={as}
        truncate
        className={cn(
          icon && 'flex items-center gap-2',
          variant === 'destructive' && 'text-destructive',
        )}
      >
        {icon && (
          <span className="shrink-0 [&>svg]:h-4 [&>svg]:w-4 text-muted-foreground">{icon}</span>
        )}
        {title}
      </PanelTitle>
      {description && collapseDescriptionOnMobile ? (
        <DescriptionHint className="sm:hidden" ariaLabel="About this section">
          {description}
        </DescriptionHint>
      ) : null}
    </div>
  );

  const descriptionNode =
    description != null ? (
      <CardDescription className={collapseDescriptionOnMobile ? 'max-sm:hidden' : undefined}>
        {description}
      </CardDescription>
    ) : null;

  return (
    <Card
      data-testid={dataTestId}
      className={cn(
        'relative gap-3 overflow-hidden border-border/30 py-4 sm:gap-4 sm:py-5',
        className,
      )}
    >
      <CardHeader
        className={cn(
          // Title + description are one cluster. CardAction defaults to
          // row-span-2, which otherwise sizes an empty second track and
          // reads as a hole between the subtitle and the body.
          'grid-rows-[auto]',
          headerClassName,
        )}
      >
        <div className="flex min-w-0 flex-col gap-1">
          {titleNode}
          {descriptionNode}
        </div>
        {action ? <CardAction className="row-span-1">{action}</CardAction> : null}
      </CardHeader>
      <CardContent className={contentClassName}>{children}</CardContent>
    </Card>
  );
}
